import React, { useMemo } from 'react';
import useResourceLoader from 'src/util/useResourceLoader';
import { useSideChannelSubscription, type SideChannel } from 'src/util/useSideChannel';
import { getObject } from 'src/util/mapObject';
import FileApi from 'src/api/FileApi';
import FileInput from 'src/components/FileInput';
import Notification from 'src/components/Notification';
import  { WFAccessToken } from 'src/api/types';

function toCleanCamelCaseIdentifier(str: string): string {
  if (!str) return '';
  const noAccents = str.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const withBoundaries = noAccents.replace(/([a-z])([A-Z])/g, '$1-$2');
  const parts = withBoundaries.split(/[^\w]+/).filter(Boolean);
  return parts
    .map((p: string, i: number) => (i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()))
    .join('');
}

interface WorkflowDocumentFieldSchema {
  title?: string;
  workflowStepName?: string;
  statePath?: string;
  resource?: string;
  buttonText?: string;
  emptyText?: string;
}

interface WorkflowDocumentFieldProps {
  formContext: { sideChannel: SideChannel<unknown[]>; wfAccessToken?: WFAccessToken };
  schema?: WorkflowDocumentFieldSchema;
  uiSchema?: { 'ui:inlinePreview'?: boolean };
}

function WorkflowDocumentField(props: WorkflowDocumentFieldProps) {
  const {
    formContext,
    schema: {
      title,
      workflowStepName,
      statePath,
      resource,
      buttonText,
      emptyText,
    } = {},
    uiSchema: {
      'ui:inlinePreview': inlinePreview = true,
    } = {},
  } = props;
  const { sideChannel, wfAccessToken } = formContext;

  const formObject = useSideChannelSubscription(sideChannel, 1) || {};

  const resolvedPath = useMemo(
    () => statePath || (workflowStepName ? `state.documents.${toCleanCamelCaseIdentifier(workflowStepName)}` : null),
    [statePath, workflowStepName]
  );

  const referenceId = useMemo(() => {
    if (!resolvedPath) return null;
    const raw = getObject(formObject, resolvedPath);
    return Array.isArray(raw) ? raw[0] : raw;
  }, [formObject, resolvedPath]);

  // eslint-disable-next-line no-console
  console.log('[WorkflowDocumentField]', {
    resolvedPath,
    referenceId,
    documentsInState: getObject(formObject, 'state.documents'),
    formObjectKeys: Object.keys(formObject || {}),
  });

  const [file, loadingFile, errorLoadingFile] = useResourceLoader(async () => {
    if (!referenceId) return null;
    return FileApi.getFileByReferenceId({ referenceId, wfAccessToken });
  }, [referenceId]);

  const fileForInput = file && file.url ? { url: file.url, name: file.name } : null;
  const placeholder = !referenceId ? (emptyText || `No document generated yet.`) : '';

  return (
    <div className="space-y-1 file-field">
      {errorLoadingFile ? <Notification error={errorLoadingFile} /> : null}
      <FileInput
        fieldTitle={title}
        file={fileForInput}
        readonly
        viewable
        inlinePreview={inlinePreview}
        resource={resource || `Document`}
        viewButtonText={buttonText}
        placeholder={placeholder}
        loadingFile={loadingFile}
        onFileSelected={() => {}}
      />
    </div>
  );
}

export default WorkflowDocumentField;
